import { interesesMensualesUSD } from "./constants";
import { crearRegistroFinancieroER } from "./registroFinancieroER";

/**
 * FUENTE ÚNICA de la línea "Intereses" del ER v2 (la que va DEBAJO del EBIT).
 *
 * Antes, el monto de intereses se recomputaba inline en ~6 sitios (fila Intereses en sus
 * 3 modos + Beneficio Neto en modoCombinado/ventana/normal + acumulados), cada uno
 * multiplicando `interesesMensualesUSD[pais]` por el TC del mes a mano. Este helper
 * centraliza ese cálculo: todos los sitios leen del array pre-computado, sin recalcular.
 *
 * Baseline (Fase 1): SOLO el hardcode de la deuda del holding (`interesesMensualesUSD`,
 * en USD/mes por país). El 2º sumando —intereses de factoring reales por mes, en moneda
 * local— es la COSTURA para la Fase 2: hoy se pasa el array de ceros (default) y la salida
 * es idéntica al comportamiento previo al peso.
 *
 * TC: reproduce exactamente el que usan los sitios hoy — `dolarMensual[m] || dolarFallback[pais]`.
 * `dolarMensual` es el mapa del PAÍS DE SESIÓN (useExchangeRates lo calcula con `pais`), así
 * que para el país local sirve en Chile y en Perú por igual (el viejo `pais==="Chile" ?
 * dolarMensual : dolarMensualPeru` era redundante: cuando `pais="Perú"`, `dolarMensual`
 * ya ES el mapa de Perú). El fallback terciario `|| getDolarFallback(pais)` que aparecía
 * en algunas celdas total es la MISMA función que alimenta `dolarFallback[pais]`, así que
 * nunca cambiaba el valor — no se replica porque no aporta.
 *
 * @param {object}   params
 * @param {string}   params.pais - "Chile" | "Perú"
 * @param {number[]} params.dolarMensual - TC del país de sesión, 12 meses
 * @param {object}   params.dolarFallback - { Chile, Perú } TC de respaldo
 * @param {number[]} [params.interesesFactoringMensual] - intereses de factoring por mes,
 *   en MONEDA LOCAL (Fase 2). Default: 12 ceros → salida idéntica a Fase 1.
 * @returns {{ usdMensual: number[], localMensual: number[] }}
 *   - usdMensual[m]   = interesesMensualesUSD[pais] + factoring_del_mes_en_USD
 *   - localMensual[m] = interesesMensualesUSD[pais] × TC_mes(m) + factoring_del_mes_local
 */
export const interesesMensualER = ({
  pais,
  dolarMensual,
  dolarFallback,
  interesesFactoringMensual = Array(12).fill(0),
}) => {
  const baselineUSD = interesesMensualesUSD[pais] || 0;
  const fb = dolarFallback?.[pais] || 0;

  const usdMensual = [];
  const localMensual = [];
  for (let m = 0; m < 12; m++) {
    const tc = dolarMensual?.[m] || fb || 0;
    const factoringLocal = Number(interesesFactoringMensual?.[m]) || 0;
    const factoringUSD = tc ? factoringLocal / tc : 0;
    usdMensual.push(baselineUSD + factoringUSD);
    localMensual.push(baselineUSD * tc + factoringLocal);
  }
  return { usdMensual, localMensual };
};

/**
 * FASE 3 PASO 4 (criterio 1), punto 2c — intereses REALES de factoring como
 * `RegistroFinancieroER[]`, uno por operación, con SU PROPIA fecha (`fecha_operacion`) —
 * Criterio 1a. Fuente: `detalle` de `interesesFactoringPorMes` (`src/utils/interesesFactoring.js`),
 * que ya trae, por mes, las operaciones que componen ese mes con `{ fecha, interes }` — este
 * helper NO recalcula nada, solo envuelve cada operación en un registro para que
 * `convertirRegistroAUSD` la convierta con el TC de SU fecha exacta (en vez del TC de un solo
 * mes dividiendo el total ya sumado, el criterio de hoy que sigue viviendo en
 * `interesesMensualER` para Perú).
 *
 * @param {object} p
 * @param {Object} p.detalle - `{ [mesIndice]: [{ fecha, interes, ... }] }` de `interesesFactoringPorMes`.
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosInteresesFactoring = ({ detalle, pais, ano }) =>
  Object.entries(detalle || {}).flatMap(([mesIndiceStr, ops]) =>
    (ops || [])
      .filter((op) => op.interes && op.fecha)
      .map((op) =>
        crearRegistroFinancieroER({
          fecha: op.fecha,
          ano,
          mesIndice: Number(mesIndiceStr),
          montoLocal: op.interes,
          bloque: "DEBAJO_EBITDA",
          concepto: "intereses_factoring",
          pais,
          meta: { origen: "factoring", proveedor: op.proveedor, folio: op.folio },
        })
      )
  );
